import type { FC, ReactNode } from 'react';
import React, { Fragment, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ProfileCardAction } from '@atlaskit/profilecard/types';
import ProfileCardTrigger from '@atlaskit/profilecard/profile-card-trigger';

import {
	UserProfileCardWithTrigger,
	type UserProfileCardAction,
} from '@atlassian/user-profile-card/trigger';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useViewUserProfile } from '@confluence/global-operations/entry-points/useViewUserProfile.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { getNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/getNavigateToTeamsApp.ts';
import { useNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/useNavigateToTeamsApp.ts';

import { FeedFollow } from './FeedFollow/FeedFollow.tsx';
import { FeedFollowAction } from './FeedFollow/FeedFollowAction.tsx';
import { useFeedFollow } from './FeedFollow/useFeedFollow.ts';
import { getProfileClient } from './getProfileClient.ts';
import { getAtlassianPeopleProfileUrl, getAtlassianPeopleProfileUrlAsync } from './helpers.ts';
import { FeedFollowErrorDisplay } from './FeedFollow/FeedFollowErrorDisplay.tsx';

const i18n = defineMessages({
	viewProfile: {
		id: 'profile.card.viewProfile',
		defaultMessage: 'View profile',
		description: 'Text for the view profile button on the profile card',
	},
});

export type TriggerPosition =
	| 'bottom-start'
	| 'bottom'
	| 'bottom-end'
	| 'left-start'
	| 'left'
	| 'left-end'
	| 'top-end'
	| 'top'
	| 'top-start'
	| 'right-end'
	| 'right'
	| 'right-start';

export type Trigger = 'click' | 'hover';

type ProfileCardWrapperProps = {
	userId?: string | null;
	position?: TriggerPosition;
	isAnonymous?: boolean;
	canViewProfile?: boolean;
	trigger?: Trigger;
	children: ReactNode;
	fullName?: string;
	disabledAriaAttributes?: boolean;
	onVisibilityChange?: (isVisible: boolean) => void;
	offset?: [number, number];
	ariaLabel?: string;
	/**
	 * When `true`, the trigger element fills the available width instead of collapsing to its
	 * content width. Use when the trigger wraps a full-width element such as a menu row. Only
	 * honoured on the migrated profile card path. Defaults to `false`.
	 */
	fullWidthTrigger?: boolean;
	/**
	 * When the subject is a Rovo agent, supply the agent-card config so the migrated profile
	 * card renders the dedicated AgentCard instead of CardError. `resourceClient` is injected by
	 * this wrapper, so callers can pass an empty object (`{}`). Only honoured on the migrated
	 * (`people-teams_migrate-user-profile-card`) path; ignored when the subject is not an agent.
	 */
	agent?: Omit<
		NonNullable<React.ComponentProps<typeof UserProfileCardWithTrigger>['agent']>,
		'resourceClient'
	>;
};

export const getViewProfileAction: (params: {
	push: (url: string, forceReload?: boolean | undefined) => void;
	userId?: string | null;
}) => ProfileCardAction = ({ push, userId }) => ({
	label: <FormattedMessage {...i18n.viewProfile} />,
	id: 'view-profile',
	callback: () => getAtlassianPeopleProfileUrlAsync(userId as string).then(push),
	shouldRender: (userData: any) => {
		// missing/deleted/corrupted users don't return any data or status
		if (!userData) return false;

		return userData.status && userData.status !== 'inactive';
	},
	link: getAtlassianPeopleProfileUrl(userId, '', CONTEXT_PATH),
});

export const getViewProfileInTeamsAppAction: (params: {
	navigate: () => void;
	url: string;
}) => ProfileCardAction = ({ navigate, url }) => ({
	label: <FormattedMessage {...i18n.viewProfile} />,
	id: 'view-profile',
	callback: navigate,
	shouldRender: (userData: any) => {
		// missing/deleted/corrupted users don't return any data or status
		if (!userData) return false;

		return userData.status && userData.status !== 'inactive';
	},
	link: url,
});

export const getFollowUserAction: (params: {
	loggedInUserId: string | null;
	getToggleFollowUser: (
		userId: string | null | undefined,
		followed: boolean,
		source: string,
	) => () => void;
	renderFeedFollow: boolean;
	userId?: string | null;
	followedUsers?: (string | null)[];
}) => ProfileCardAction = ({
	loggedInUserId,
	getToggleFollowUser,
	renderFeedFollow,
	userId,
	followedUsers,
}) => {
	const followed = !!(userId && followedUsers?.includes(userId));

	return {
		label: <FeedFollow followed={followed} />,
		id: 'feed-follow-user',
		callback: getToggleFollowUser(userId, followed, 'profileCard'),
		shouldRender: (userData) =>
			renderFeedFollow &&
			Boolean(userId) &&
			userId !== loggedInUserId &&
			userData?.status === 'active',
	};
};

export const getFollowUserActionNext: (params: {
	loggedInUserId: string | null;
	getToggleFollowUser: (
		userId: string | null | undefined,
		followed: boolean,
		source: string,
	) => () => void;
	renderFeedFollow: boolean;
	followedRef: { current: boolean };
	userId?: string | null;
}) => UserProfileCardAction = ({
	loggedInUserId,
	getToggleFollowUser,
	renderFeedFollow,
	followedRef,
	userId,
}) => ({
	label: <FeedFollowAction userId={userId} />,
	id: 'feed-follow-user',
	callback: () => getToggleFollowUser(userId, followedRef.current, 'profileCard')(),
	shouldRender: () => renderFeedFollow && Boolean(userId) && userId !== loggedInUserId,
});

export const ProfileCardWrapper: FC<ProfileCardWrapperProps> = ({
	position = 'bottom-start',
	userId,
	isAnonymous = false,
	canViewProfile = true,
	children,
	trigger = 'hover',
	fullName,
	disabledAriaAttributes,
	onVisibilityChange,
	offset,
	ariaLabel,
	agent,
	fullWidthTrigger,
}) => {
	const { canViewUserProfile: hasViewUserProfilePermission } = useViewUserProfile();

	const { userId: loggedInUserId, cloudId, featureFlags, orgId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const reportingLines = useNavigateToTeamsApp({
		type: 'LANDING',
	});

	const getReportingLinesUserProfile = (clickedUserId: string) =>
		getNavigateToTeamsApp(orgId || '', {
			type: 'USER',
			payload: {
				userId: clickedUserId,
			},
		});

	const userProfile = useNavigateToTeamsApp({
		type: 'USER',
		payload: {
			userId: userId || '',
		},
	});

	const reportingLinesProfileUrl = reportingLines.href;

	const { feedConfigError, followedUsers, getToggleFollowUser, mutationError, renderFeedFollow } =
		useFeedFollow();

	const viewProfileAction = useMemo(
		() =>
			getViewProfileInTeamsAppAction({
				navigate: userProfile.onNavigate,
				url: userProfile.href,
			}),
		[userProfile],
	);

	const followUserAction = useMemo(
		() =>
			getFollowUserAction({
				loggedInUserId,
				getToggleFollowUser,
				renderFeedFollow,
				userId,
				followedUsers,
			}),
		[loggedInUserId, getToggleFollowUser, renderFeedFollow, userId, followedUsers],
	);

	// The card snapshots the actions, so the follow callback reads the latest follow state
	// from this ref (updated on every render) instead of a value captured when the action
	// was built. This keeps repeated follow/unfollow toggles in the correct direction.
	const followedRef = useRef(false);
	followedRef.current = !!(userId && followedUsers?.includes(userId));

	const followUserActionNext = useMemo(
		() =>
			getFollowUserActionNext({
				loggedInUserId,
				getToggleFollowUser,
				renderFeedFollow,
				followedRef,
				userId,
			}),
		[loggedInUserId, getToggleFollowUser, renderFeedFollow, userId],
	);

	// Should display profile?
	if (
		isAnonymous ||
		!canViewProfile ||
		!userId ||
		!loggedInUserId ||
		!hasViewUserProfilePermission
	) {
		return <>{children}</>;
	}

	const reportingLinesClickCallback = (clickedUserId: string) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'profileCardWrapper',
				action: 'clicked',
				actionSubject: 'viewProfile',
				actionSubjectId: 'userProfileCard',
				attributes: {
					clickTarget: 'userReportingLine',
				},
			},
		}).fire();
		getReportingLinesUserProfile(clickedUserId).onNavigate();
		return false;
	};

	if (
		fg('people-teams_migrate-user-profile-card') ||
		expValEquals('pt_user_profile_card_migration_exp', 'isEnabled', true)
	) {
		return (
			<Fragment>
				<FeedFollowErrorDisplay
					feedConfigError={feedConfigError}
					mutationError={mutationError}
					showErrorFlag
				/>
				<UserProfileCardWithTrigger
					cloudId={cloudId}
					orgId={orgId || ''}
					userId={userId}
					viewerId={loggedInUserId}
					placement={position}
					offset={offset}
					trigger={trigger}
					fullWidthTrigger={fullWidthTrigger}
					actions={[followUserActionNext]}
					agent={
						agent
							? { ...agent, resourceClient: getProfileClient(cloudId, featureFlags) }
							: undefined
					}
					{...(disabledAriaAttributes ? {} : { ariaLabel })}
					testId="profileCardTrigger"
				>
					{children}
				</UserProfileCardWithTrigger>
			</Fragment>
		);
	}

	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => (
				<Fragment>
					<FeedFollowErrorDisplay
						feedConfigError={feedConfigError}
						mutationError={mutationError}
						showErrorFlag
					/>
					<ProfileCardTrigger
						cloudId={cloudId}
						userId={userId}
						resourceClient={getProfileClient(cloudId, featureFlags)}
						position={position}
						trigger={trigger}
						actions={[viewProfileAction, followUserAction]}
						reportingLinesProfileUrl={reportingLinesProfileUrl}
						onReportingLinesClick={(user) => reportingLinesClickCallback(user.accountIdentifier)}
						addFlag={(flag) => flags.showFlag(flag)}
						prepopulatedData={{
							fullName,
						}}
						disabledAriaAttributes={disabledAriaAttributes}
						onVisibilityChange={onVisibilityChange}
						offset={offset}
						testId="profileCardTrigger"
						viewingUserId={loggedInUserId}
						{...(disabledAriaAttributes ? {} : { ariaLabel })}
					>
						{children}
					</ProfileCardTrigger>
				</Fragment>
			)}
		</Subscribe>
	);
};
